import ReactDOM from "react-dom/client";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import {
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth-store";
import { captureServerError } from "@/lib/capture-server-error";
import { handleServerError } from "@/lib/handle-server-error";
import { initSentry } from "@/config/sentry.config";
import { DirectionProvider } from "./context/direction-provider";
import { FontProvider } from "./context/font-provider";
import { ThemeProvider } from "./context/theme-provider";
import { routeTree } from "./routeTree.gen";
import "./styles/index.css";

initSentry();

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: (failureCount, error) => {
				// eslint-disable-next-line no-console
				if (import.meta.env.DEV) console.log({ failureCount, error });

				if (failureCount >= 0 && import.meta.env.DEV) return false;
				if (failureCount > 3 && import.meta.env.PROD) return false;

				return !(
					error instanceof AxiosError &&
					[401, 403].includes(error.response?.status ?? 0)
				);
			},
			refetchOnWindowFocus: import.meta.env.PROD,
			staleTime: 10 * 1000, // 10s
		},
		mutations: {
			onError: (error) => {
				handleServerError(error);

				if (error instanceof AxiosError) {
					if (error.response?.status === 304) {
						toast.error("Content not modified!");
					}
				}
			},
		},
	},
	queryCache: new QueryCache({
		onError: (error) => {
			captureServerError(error);

			if (error instanceof AxiosError) {
				if (error.response?.status === 401) {
					toast.error("Session expired!");
					useAuthStore.getState().auth.reset();
					const redirect = `${router.history.location.href}`;
					router.navigate({ to: "/sign-in", search: { redirect } });
				}
				if (error.response?.status === 500) {
					toast.error("Internal Server Error!");
					router.navigate({ to: "/500" });
				}
				if (error.response?.status === 403) {
					// router.navigate("/forbidden", { replace: true });
				}
			}
		},
	}),
});

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<Sentry.ErrorBoundary
			fallback={({ error }) => (
				<div style={{ padding: "2rem", textAlign: "center" }}>
					<h1>An error occurred</h1>
					<p>{error instanceof Error ? error.message : "Unknown error"}</p>
					<button onClick={() => window.location.reload()}>
						Reload page
					</button>
				</div>
			)}
			showDialog
		>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider>
					<FontProvider>
						<DirectionProvider>
							<RouterProvider router={router} />
						</DirectionProvider>
					</FontProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</Sentry.ErrorBoundary>
	);
}
